import React, { useEffect, useState } from "react";
import { fetchGrammarTopics, fetchGrammarTopicById } from "../../blog/grammar/adminGrammarService";
import { fetchGdTopics } from "../../gd/adminGdService";
import { fetchReadingPassages } from "../../blog/reading/adminReadingService";

const lbl = {
  display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569",
  marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.03em",
};
const inp = {
  width: "100%", padding: "8px 11px", borderRadius: 7, border: "1px solid #e2e8f0",
  fontSize: "0.875rem", color: "#0f172a", background: "#fff", boxSizing: "border-box",
};

// ── Selected chip ─────────────────────────────────────────────────────────────
const SelectedChip = ({ label, subLabel, onClear }) => (
  <div className="tt-selected-chip">
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: "0.82rem", color: "#1d4ed8" }}>{label}</div>
      {subLabel && <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 1 }}>{subLabel}</div>}
    </div>
    <button type="button" onClick={onClear} title="Clear">✕</button>
  </div>
);

// ── Grammar: Topic → Sub-topic cascade ───────────────────────────────────────
const GrammarPicker = ({ value, onChange }) => {
  const [topics, setTopics]               = useState([]);
  const [topicSearch, setTopicSearch]     = useState("");
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [subTopics, setSubTopics]         = useState([]);
  const [loadingSub, setLoadingSub]       = useState(false);

  useEffect(() => {
    fetchGrammarTopics({ page: 0, size: 100 }).then((r) => {
      if (r.status === 200) setTopics(r.data?.content || r.data || []);
    });
  }, []);

  const handleTopicSelect = async (topic) => {
    setSelectedTopic(topic);
    onChange(null);
    setLoadingSub(true);
    const res = await fetchGrammarTopicById(topic.id);
    if (res.status === 200) setSubTopics(res.data?.subTopics || res.data?.data?.subTopics || []);
    setLoadingSub(false);
  };

  const clearTopic = () => { setSelectedTopic(null); setSubTopics([]); onChange(null); };

  const filteredTopics = topics.filter((t) =>
    t.title?.toLowerCase().includes(topicSearch.toLowerCase())
  );

  return (
    <div>
      <label style={lbl}>Grammar Topic</label>
      {selectedTopic ? (
        <SelectedChip label={selectedTopic.title} onClear={clearTopic} />
      ) : (
        <div style={{ marginBottom: 10 }}>
          <input style={{ ...inp, marginBottom: 6 }} placeholder="Search topic…"
            value={topicSearch} onChange={(e) => setTopicSearch(e.target.value)} />
          <select style={inp} value="" onChange={(e) => {
            const t = topics.find((x) => String(x.id) === e.target.value);
            if (t) handleTopicSelect(t);
          }}>
            <option value="" disabled>— Select a topic —</option>
            {filteredTopics.map((t) => (
              <option key={t.id} value={t.id}>{t.icon ? `${t.icon} ` : ""}{t.title}</option>
            ))}
          </select>
        </div>
      )}

      {selectedTopic && (
        <>
          <label style={{ ...lbl, marginTop: 10 }}>Grammar Sub-Topic</label>
          {loadingSub ? (
            <p style={{ fontSize: "0.8rem", color: "#94a3b8", margin: "4px 0 10px" }}>Loading sub-topics…</p>
          ) : value ? (
            <SelectedChip label={value.label} onClear={() => onChange(null)} />
          ) : subTopics.length === 0 ? (
            <p style={{ fontSize: "0.8rem", color: "#94a3b8", margin: "4px 0 10px" }}>No sub-topics for this topic.</p>
          ) : (
            <select style={inp} value="" onChange={(e) => {
              const st = subTopics.find((x) => String(x.id) === e.target.value);
              if (st) onChange({ id: st.id, label: st.title });
            }}>
              <option value="" disabled>— Select a sub-topic —</option>
              {subTopics.map((st) => (
                <option key={st.id} value={st.id}>{st.title}</option>
              ))}
            </select>
          )}
        </>
      )}
    </div>
  );
};

// ── GD Picker ─────────────────────────────────────────────────────────────────
const GdPicker = ({ value, onChange }) => {
  const [topics, setTopics]   = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGdTopics({ page: 0, size: 200 }).then((r) => {
      if (r.status === 200) setTopics(r.data?.content || r.data || []);
      setLoading(false);
    });
  }, []);

  const filtered = topics.filter((t) =>
    t.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <label style={lbl}>GD Topic</label>
      {value ? (
        <SelectedChip label={value.label} subLabel={value.subLabel} onClear={() => onChange(null)} />
      ) : (
        <div>
          <input style={{ ...inp, marginBottom: 6 }} placeholder="Search GD topic…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <select style={inp} value="" disabled={loading} onChange={(e) => {
            const t = topics.find((x) => String(x.id) === e.target.value);
            if (t) onChange({
              id: t.id,
              label: t.title,
              subLabel: t.difficulty
                ? `${t.difficulty}${t.category?.name ? " · " + t.category.name : ""}`
                : t.category?.name || "",
            });
          }}>
            <option value="" disabled>
              {loading ? "Loading…" : filtered.length === 0 ? "No topics found" : "— Select a GD topic —"}
            </option>
            {filtered.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}{t.difficulty ? ` (${t.difficulty})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

// ── Reading Picker ────────────────────────────────────────────────────────────
const ReadingPicker = ({ value, onChange }) => {
  const [passages, setPassages] = useState([]);
  const [search, setSearch]     = useState("");
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    fetchReadingPassages({ page: 0, size: 200 }).then((r) => {
      if (r.status === 200) setPassages(r.data?.content || r.data || []);
      setLoading(false);
    });
  }, []);

  const filtered = passages.filter((p) =>
    p.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <label style={lbl}>Reading Passage</label>
      {value ? (
        <SelectedChip label={value.label} subLabel={value.subLabel} onClear={() => onChange(null)} />
      ) : (
        <div>
          <input style={{ ...inp, marginBottom: 6 }} placeholder="Search reading passage…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <select style={inp} value="" disabled={loading} onChange={(e) => {
            const p = passages.find((x) => String(x.id) === e.target.value);
            if (p) onChange({
              id: p.id,
              label: p.title,
              subLabel: [p.difficulty, p.category].filter(Boolean).join(" · "),
            });
          }}>
            <option value="" disabled>
              {loading ? "Loading…" : filtered.length === 0 ? "No passages found" : "— Select a passage —"}
            </option>
            {filtered.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}{p.difficulty ? ` (${p.difficulty})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

// ── Main ContentPicker ────────────────────────────────────────────────────────
const ContentPicker = ({ contentType, contentId, contentLabel, onTypeChange, onContentChange }) => {
  const currentValue = contentId ? { id: contentId, label: contentLabel || `ID: ${contentId}` } : null;

  return (
    <div>
      <label style={lbl}>
        Content Type{" "}
        <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span>
      </label>
      <select
        style={{ ...inp, marginBottom: contentType ? 14 : 0 }}
        value={contentType}
        onChange={(e) => { onTypeChange(e.target.value); onContentChange(null); }}
      >
        <option value="">— None —</option>
        <option value="GD">Group Discussion</option>
        <option value="GRAMMAR">Grammar</option>
        <option value="READING">Reading Passage</option>
      </select>

      {contentType === "GRAMMAR" && <GrammarPicker value={currentValue} onChange={onContentChange} />}
      {contentType === "GD"      && <GdPicker      value={currentValue} onChange={onContentChange} />}
      {contentType === "READING" && <ReadingPicker value={currentValue} onChange={onContentChange} />}
    </div>
  );
};

export default ContentPicker;
